// Copyright (c) 2026 Signal from Noise, Inc. All rights reserved.

import {
  AddChiefSessionChatRequestConfig,
  type ChiefLiveEvent,
  type ChiefLiveSubscription,
  type ChiefPublishedMedia,
  type ChiefSession,
  type ChiefSessionMediaItem,
  type ChiefSessionMeta,
  type ChiefSessionNameResponse,
  type ChiefSuggestedPrompt,
  DeleteChiefSessionRequestConfig,
  GetChiefSessionRequestConfig,
  ListChiefSessionMediaRequestConfig,
  ListChiefSessionsRequestConfig,
  MoveChiefSessionsRequestConfig,
  type MoveChiefSessionsResponse,
  PublishChiefSessionMediaRequestConfig,
  RestartChiefSessionRequestConfig,
  SuggestChiefSessionPromptsRequestConfig,
  subscribeToChiefLiveSession,
  UpdateChiefSessionNameRequestConfig,
  useStorytellClient,
} from "@storytell/client";
import { err, ok, type Result } from "neverthrow";
import type { PaginatedList } from "./MemoryService";

export type {
  ChiefSession as LiveSession,
  ChiefSessionAction as LiveSessionAction,
  ChiefSessionContextItem as LiveSessionContextItem,
  ChiefSessionDecision as LiveSessionDecision,
  ChiefSessionMediaItem as LiveSessionMedia,
  ChiefSessionMeta as LiveSessionMeta,
  ChiefSessionRelay as LiveSessionRelay,
  ChiefSessionTurn as LiveSessionTurn,
  ChiefSuggestedPrompt as LiveSessionSuggestedPrompt,
} from "@storytell/client";
export { ChiefSessionStates as LiveSessionStates } from "@storytell/client";

export interface ListLiveSessionsOptions {
  limit?: number;
  afterId?: string;
  beforeId?: string;
}

interface LiveSessionsServiceConfig {
  authReady: () => boolean;
  authToken: () => string | undefined;
  projectId: () => string | undefined;
}

export interface LiveSessionsService {
  list(opts?: ListLiveSessionsOptions): Promise<Result<PaginatedList<ChiefSessionMeta>, Error>>;
  get(sessionId: string): Promise<Result<ChiefSession, Error>>;
  subscribe(
    sessionId: string,
    callbacks: {
      onEvent: (event: ChiefLiveEvent) => void;
      onError?: (error: Error) => void;
    }
  ): ChiefLiveSubscription;
  delete(sessionId: string): Promise<Result<undefined, Error>>;
  move(sessionIds: string[], projectId: string): Promise<Result<MoveChiefSessionsResponse, Error>>;
  restart(sessionId: string): Promise<Result<ChiefSession, Error>>;
  updateName(sessionId: string, name: string): Promise<Result<ChiefSessionNameResponse, Error>>;
  recordChat(sessionId: string, chat: { chatId: string; title: string }): Promise<Result<ChiefSession, Error>>;
  listMedia(sessionId: string): Promise<Result<ChiefSessionMediaItem[], Error>>;
  publishMedia(sessionId: string, assetIds: string[]): Promise<Result<ChiefPublishedMedia[], Error>>;
  suggestPrompts(sessionId: string, record: SessionRecord): Promise<Result<ChiefSuggestedPrompt[], Error>>;
}

/** What the session itself said, as the send-to-agent dialog has it on screen. */
export interface SessionRecord {
  brief: string;
  summary: string;
  toDos: string[];
  decisions: string[];
  transcript: string;
}

const authNotReadyError = (): Error => new Error("Auth not ready");

export function createLiveSessionsService(config: LiveSessionsServiceConfig): LiveSessionsService {
  const client = useStorytellClient();

  return {
    async list(opts) {
      if (!config.authReady()) return err(authNotReadyError());
      const request = ListChiefSessionsRequestConfig.withBearerToken(config.authToken());
      const result = await client.fetch(
        request,
        { limit: opts?.limit, afterId: opts?.afterId, beforeId: opts?.beforeId },
        undefined
      );
      if (result.isErr()) return err(result.error);
      return ok({
        data: result.value.data,
        firstId: result.value.first_id,
        lastId: result.value.last_id,
        hasMore: result.value.has_more,
      });
    },
    async get(sessionId) {
      if (!config.authReady()) return err(authNotReadyError());
      const request = GetChiefSessionRequestConfig.withBearerToken(config.authToken());
      return client.fetch(request, { sessionId }, undefined);
    },
    subscribe(sessionId, callbacks) {
      return subscribeToChiefLiveSession(sessionId, {
        authToken: config.authToken,
        projectId: config.projectId,
        onEvent: callbacks.onEvent,
        onError: callbacks.onError,
      });
    },
    async delete(sessionId) {
      if (!config.authReady()) return err(authNotReadyError());
      const request = DeleteChiefSessionRequestConfig.withBearerToken(config.authToken());
      return client.fetch(request, { sessionId }, undefined);
    },
    async move(sessionIds, projectId) {
      if (!config.authReady()) return err(authNotReadyError());
      const request = MoveChiefSessionsRequestConfig.withBearerToken(config.authToken());
      return client.fetch(request, undefined, { sessionIds, projectId });
    },
    async restart(sessionId) {
      if (!config.authReady()) return err(authNotReadyError());
      const request = RestartChiefSessionRequestConfig.withBearerToken(config.authToken());
      return client.fetch(request, { sessionId }, undefined);
    },
    async updateName(sessionId, name) {
      if (!config.authReady()) return err(authNotReadyError());
      const request = UpdateChiefSessionNameRequestConfig.withBearerToken(config.authToken());
      return client.fetch(request, { sessionId }, { name });
    },
    async recordChat(sessionId, chat) {
      if (!config.authReady()) return err(authNotReadyError());
      const request = AddChiefSessionChatRequestConfig.withBearerToken(config.authToken());
      return client.fetch(request, { sessionId }, chat);
    },
    async listMedia(sessionId) {
      if (!config.authReady()) return err(authNotReadyError());
      const request = ListChiefSessionMediaRequestConfig.withBearerToken(config.authToken());
      const result = await client.fetch(request, { sessionId }, undefined);
      if (result.isErr()) return err(result.error);
      return ok(result.value.items);
    },
    async publishMedia(sessionId, assetIds) {
      if (!config.authReady()) return err(authNotReadyError());
      const request = PublishChiefSessionMediaRequestConfig.withBearerToken(config.authToken());
      const result = await client.fetch(request, { sessionId }, { assetIds });
      if (result.isErr()) return err(result.error);
      return ok(result.value.items);
    },
    async suggestPrompts(sessionId, record) {
      if (!config.authReady()) return err(authNotReadyError());
      const request = SuggestChiefSessionPromptsRequestConfig.withBearerToken(config.authToken());
      const result = await client.fetch(request, { sessionId }, record);
      if (result.isErr()) return err(result.error);
      return ok(result.value.prompts);
    },
  };
}
